/**
 * Charleye scroll reveal
 *
 * Tags content inside each section and eases it in as it scrolls into view.
 * Sections opt out with `data-ch-reveal-skip` on the section element.
 *
 * The pairing with charleye-reveal.css matters: the CSS only hides things
 * once this script has added `.ch-reveal-ready` to <html>, so a failure here
 * leaves a normal, fully visible page rather than a blank one.
 */

const READY_CLASS = 'ch-reveal-ready';
const IN_CLASS = 'ch-reveal-in';
const MAX_STAGGER_STEPS = 5;

/** Containers whose contents animate themselves, or must never be hidden. */
const EXCLUDED_ANCESTORS = [
  '#header-group',
  'dialog',
  '.cart-drawer',
  '.header',
  '.charleye-hero',
  'slideshow-component',
  'marquee-component',
  '[data-ch-reveal-skip]',
].join(',');

const TEXT_SELECTOR = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'p',
  '.button',
  'a.button',
  'button.button',
  'blockquote',
  'ul',
  'ol',
].join(',');

const MEDIA_SELECTOR = ['picture', 'img', 'video', 'svg.placeholder-svg'].join(',');

/**
 * Tiles that own their contents. A product-grid card is listed here so the
 * reveal animates the card as one piece and leaves its photos alone: the
 * photos after the first sit off to the side in a swipe track, never cross the
 * viewport, and would otherwise stay hidden until swiped to. The newsletter
 * form is listed so the input and its button arrive together — inputs are not
 * in the text selector, and the button alone fading in beside a static field
 * looks broken.
 */
const CARD_SELECTOR = [
  '.product-card',
  '.card',
  '.collection-card',
  '.blog-post-card',
  '.charleye-grid__card',
  '.charleye-newsletter__form',
  '.charleye-categories__track',
].join(',');

/** A single reduced-motion check, reused everywhere. */
const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Elements are only tagged if no already-tagged element contains them —
 * otherwise a heading inside an animating card would fade in twice, at two
 * different speeds, which reads as a glitch rather than a flourish.
 */
function isNestedInTagged(element, tagged) {
  return tagged.some((candidate) => candidate !== element && candidate.contains(element));
}

function collectTargets(section) {
  const tagged = [];

  const consider = (selector, kind) => {
    for (const element of section.querySelectorAll(selector)) {
      if (element.closest(EXCLUDED_ANCESTORS)) continue;
      if (element.hasAttribute('data-ch-reveal')) continue;
      if (isNestedInTagged(element, tagged)) continue;

      // Skip anything with no box to animate (hidden tabs, empty wrappers).
      const rect = element.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) continue;

      element.setAttribute('data-ch-reveal', kind);
      tagged.push(element);
    }
  };

  // Cards first, then media, then text: the broadest containers claim their
  // contents before the narrower selectors can tag them individually.
  consider(CARD_SELECTOR, 'card');
  consider(MEDIA_SELECTOR, 'media');
  consider(TEXT_SELECTOR, 'text');

  // Document order, so the stagger runs down the page rather than by selector.
  return tagged.sort((a, b) =>
    a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
  );
}

/**
 * The stagger lives in charleye-reveal.css so the pacing is tuned in one
 * place. Read once — it does not change between frames.
 */
let staggerMs;

function getStaggerMs() {
  if (staggerMs !== undefined) return staggerMs;

  const declared = getComputedStyle(document.documentElement)
    .getPropertyValue('--ch-reveal-stagger')
    .trim();

  staggerMs = declared.endsWith('ms') ? parseFloat(declared) : parseFloat(declared) * 1000;
  if (!Number.isFinite(staggerMs)) staggerMs = 65;

  return staggerMs;
}

function reveal(element) {
  const step = Number(element.dataset.chRevealStep || 0);
  element.style.setProperty('--ch-reveal-delay', `${step * getStaggerMs()}ms`);
  element.classList.add(IN_CLASS);

  // Drop the inline delay once the entrance is done, so a later re-render
  // (filters, cart updates) does not inherit a stale stagger.
  element.addEventListener(
    'transitionend',
    () => element.style.removeProperty('--ch-reveal-delay'),
    { once: true }
  );
}

let observer;

function getObserver() {
  if (observer) return observer;

  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        reveal(entry.target);
        observer.unobserve(entry.target);
        pending.delete(entry.target);
      }
    },
    {
      // Start the entrance a little before the element is fully on screen, so
      // it is already settling by the time it is comfortably in view.
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.01,
    }
  );

  return observer;
}

/** Elements still waiting for their entrance. */
const pending = new Set();

/**
 * The observer's -8% bottom margin means an element has to rise a little
 * above the bottom edge before it animates. The last lines of the page (the
 * footer's copyright bar) can never rise that far — the page runs out first —
 * so at the bottom of the page anything already on screen is revealed.
 */
function revealAtPageEnd() {
  if (pending.size === 0) return;

  // Horizon scrolls `.page-wrapper`, not the window (html and body are
  // overflow: hidden), so measure whichever element actually scrolls.
  const scroller = document.querySelector('.page-wrapper') || document.scrollingElement;
  if (!scroller) return;
  const scrolledTo = scroller.scrollTop + scroller.clientHeight;
  if (scrolledTo < scroller.scrollHeight - 4) return;

  for (const element of pending) {
    if (element.getBoundingClientRect().top < window.innerHeight) {
      reveal(element);
      observer?.unobserve(element);
      pending.delete(element);
    }
  }
}

function observeSection(section) {
  if (prefersReducedMotion()) return;

  const targets = collectTargets(section);

  // Releases the section-level hold in charleye-reveal.css. Set after tagging
  // so the section and its held-back contents are revealed in one frame.
  section.setAttribute('data-ch-reveal-section', '');

  targets.forEach((element, index) => {
    // Stagger within a section, capped so a long product grid does not end up
    // with a visibly late final row.
    element.dataset.chRevealStep = String(Math.min(index, MAX_STAGGER_STEPS));
    getObserver().observe(element);
    pending.add(element);
  });
}

function sections() {
  return document.querySelectorAll('main .shopify-section, footer .shopify-section');
}

function init() {
  // The inline snippet in the head has already added READY_CLASS optimistically,
  // so that sections are held back before they can paint. If this browser cannot
  // run the reveal, take it back off and leave an ordinary page.
  if (!('IntersectionObserver' in window) || prefersReducedMotion()) {
    document.documentElement.classList.remove(READY_CLASS);
    return;
  }

  document.documentElement.classList.add(READY_CLASS);
  window.__chRevealReady = true;
  sections().forEach(observeSection);

  // Scroll events do not bubble; capturing on the document catches them from
  // `.page-wrapper` as well as from the window.
  document.addEventListener('scroll', revealAtPageEnd, { capture: true, passive: true });
  window.addEventListener('resize', revealAtPageEnd, { passive: true });
  // A page short enough to fit the screen is already at its end.
  requestAnimationFrame(revealAtPageEnd);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

// The theme editor swaps section markup in place; re-tag whatever it replaced.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) observeSection(event.target);
});
